"use client";
import NewsActionSection, {
  useNewsSearchAndFilter,
} from "@c/newsMedia/allNews/news-action-section";
import NewsListing, {
  NewsListingProps,
} from "@c/newsMedia/allNews/news-listing";
import { RouteParams } from "@/app/[locale]/route.type";
import { getAllNewsData } from "@/cms-helpers/api-helper.client";
import { useMemo } from "react";
import useSwr from "swr";
import { formatDate } from "@util/helpers";
import { map, remove } from "lodash";
import { Button, Typography } from "@mui/material";
import SectionWrapper from "@cc/section-wrapper";
import { H6 } from "@theme/components/typography.fontvariant";
import { NewsAndMediaAPI } from "@/apiTypes/news-and-media-page-api.type";

export interface AllNewsPageProps {
  params: RouteParams;
  searchSectionProperty?: NewsAndMediaAPI["data"]["page"]["newsSearchSection"];
}

const AllNews = (props: AllNewsPageProps) => {
  const {
    params: { locale },
    searchSectionProperty = {} as AllNewsPageProps["searchSectionProperty"],
  } = props;

  const { filters, setSearchFilters } = useNewsSearchAndFilter();

  const { data: allNews, isLoading } = useSwr(
    {
      locale,
      filters,
    },
    ({ locale, filters }) => getAllNewsData(locale, filters),
  );

  const newsListData: NewsListingProps["data"] | undefined = useMemo(() => {
    if (!allNews || !allNews.data || !allNews.data.length) return undefined;
    const { data: newsData } = allNews;
    remove(
      newsData,
      (item) =>
        !item.Title ||
        !item.associatedCompany ||
        !item.associatedCompany.title ||
        !item.associateBy ||
        !item.newsDate ||
        !item.image ||
        !item.shortContent ||
        !item.slug ||
        !item.image.url ||
        !item.minRead,
    );
    return map(newsData, (item) => {
      return {
        title: item.Title,
        company: item.associatedCompany.title,
        publication: item.associateBy,
        timeToRead: item.minRead,
        date: formatDate(item.newsDate),
        image: item.image.url,
        description: item.shortContent,
        href: `/news-media/${item.newsType.toLowerCase()}/${item.slug}`,
        tag: item.newsType,
      };
    }).filter((item) => {
      return (
        item.title &&
        item.company &&
        item.publication &&
        item.timeToRead &&
        item.date &&
        item.image &&
        item.description &&
        item.href &&
        item.tag
      );
    }) as NewsListingProps["data"];
  }, [allNews]);

  return (
    <>
      <NewsActionSection
        title={searchSectionProperty?.searchTitle}
        description={searchSectionProperty?.searchDessscription}
        placeholder={searchSectionProperty?.searchPlaceholder}
      />
      {newsListData ? (
        <NewsListing locale={locale} data={newsListData} />
      ) : isLoading ? null : (
        <SectionWrapper>
          <Typography fontSize={H6} color={"primary"} gutterBottom>
            No news found
          </Typography>
          <Button
            variant={"contained"}
            onClick={() => {
              setSearchFilters({
                keyword: "",
                organization: "",
                year: "",
                sort: "desc",
              });
            }}
          >
            reset filter
          </Button>
        </SectionWrapper>
      )}
    </>
  );
};

export default AllNews;
